/**
 * lib/feeStatus.ts
 * ------------------------------------------------------------------
 * Resolve HOW a pupil's fees are handled this term, into one of a few
 * prescribed modes — so the same answer drives the class fee overview, the
 * student profile's Fees tab, and the "unaccounted" alert. Pure.
 *
 * A pupil is ACCOUNTED FOR if they are: invoiced, a staff child (billed via a
 * parent's payroll), or legitimately not billed this term (their band bills only
 * certain terms) or has no fee structure. Anything else — enrolled, billable
 * this term, but with no invoice and no other handling — is UNACCOUNTED and
 * needs the bursar's attention (a mid-term joiner who slipped through billing).
 * ------------------------------------------------------------------ */
import type { Invoice, FeeStructure } from '@/lib/financeCore';
import type { TermKey } from '@/lib/school.config';

export type FeeHandling =
  | 'invoiced'
  /* Al-Nur also carries 'staff_payroll' here. LKC has deliberately not
     settled its staff-fee policy, and a mode nobody can be put into
     would make the register read as though somebody had checked. */
  | 'not_billed_this_term'
  | 'no_structure'
  | 'unaccounted';

export type FeeTone = 'green' | 'blue' | 'amber' | 'red' | 'slate';

export interface FeeStatus {
  handling: FeeHandling;
  label: string;
  tone: FeeTone;
  /** True only for 'unaccounted' — the alert condition. */
  needsAttention: boolean;
  total?: number;
  paid?: number;
  balance?: number;
  detail?: string;
}

export interface FeeStatusInput {
  /** The pupil's invoice for this (year, term), if one exists. */
  invoice?: Invoice;
  /** The fee structure for the pupil's band + year, if configured. */
  structure?: FeeStructure;
  term: TermKey;
}

export function resolveFeeStatus(input: FeeStatusInput): FeeStatus {
  const { invoice, structure, term } = input;

  if (invoice) {
    const total = Number(invoice.total) || 0;
    const paid = Number(invoice.paid) || 0;
    const balance = invoice.balance != null ? Number(invoice.balance) : Math.max(0, total - paid);
    const paidUp = balance <= 0.005;
    return {
      handling: 'invoiced',
      label: paidUp ? 'Invoiced · settled' : 'Invoiced · balance due',
      tone: paidUp ? 'green' : 'amber',
      needsAttention: false,
      total, paid, balance,
      detail: `Invoice ${invoice.invoiceNo ?? invoice.id}`,
    };
  }

  if (!structure) {
    return { handling: 'no_structure', label: 'No fee structure for this band', tone: 'slate', needsAttention: false, detail: 'Set the band’s fee structure under Fees → Structures.' };
  }

  const billedThisTerm = !structure.terms || structure.terms.length === 0 || structure.terms.includes(term);
  if (!billedThisTerm) {
    return { handling: 'not_billed_this_term', label: 'Not billed this term', tone: 'slate', needsAttention: false, detail: 'This band is billed in other terms only.' };
  }

  return {
    handling: 'unaccounted',
    label: 'Not accounted for — needs billing',
    tone: 'red',
    needsAttention: true,
    detail: 'Enrolled and billable this term, but no invoice and not on any other fee arrangement.',
  };
}

export const FEE_HANDLING_LABELS: Record<FeeHandling, string> = {
  invoiced: 'Invoiced',
  not_billed_this_term: 'Not billed this term',
  no_structure: 'No structure',
  unaccounted: 'Unaccounted',
};
